import React, { useState, useEffect, useReducer } from "react";
import dayjs from "dayjs";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  HourglassEmpty,
  NotInterested,
  PriorityHigh,
  LowPriority,
  Remove,
} from "@mui/icons-material";
import NoData from "components/NoData";
import { I18n } from "@aws-amplify/core";
import useMediaQuery from "@mui/material/useMediaQuery";
import currentUserToken from "CurrentUserToken";
import { get } from "aws-amplify/api";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;

    return { audit: audit, fetched: true };
  }
  return lastState;
};

const CaseInfo = ({ data }) => {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });

  const formatTimestamp = (timestamp) => dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");

  const headerCells = [
    {
      id: "Status",
      label: I18n.get("Status"),
      rendercell: ({ value }) => {
        let icon;
        let color;
  
        switch (value.toLowerCase()) {
          case "in progress":
            icon = <HourglassEmpty />;
            color = "warning.main";
            break;
          case "active":
            icon = <CheckCircle />;
            color = "success.main";
            break;
          case "inactive":
            icon = <NotInterested />;
            color = "error.main";
            break;
          default:
            icon = <HourglassEmpty />;
            color = "warning.main";
            break;
        }
  
        return (
          <Box display="flex" alignItems="center">
            <Box color={color} mt={0.5}>
              {icon}
            </Box>
            <Typography variant="body2" sx={{ ml: 1 }}>
              {value}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "Priority",
      label: I18n.get("Priority"),
      rendercell: ({ value }) => {
        let icon;
        let color;
  
        switch (value.toLowerCase()) {
          case "low":
            icon = <LowPriority />;
            color = "success.main";
            break;
          case "medium":
            icon = <LowPriority />;
            color = "warning.main";
            break;
          case "high":
            icon = <PriorityHigh />;
            color = "error.main";
            break;
          default:
            icon = <Remove />;
            color = "text.secondary";
            break;
        }
  
        return (
          <Box display="flex" alignItems="center">
            <Box color={color} mt={0.5}>
              {icon}
            </Box>
            <Typography variant="body2" sx={{ ml: 1 }}>
              {value}
            </Typography>
          </Box>
        );
      },
    },
    { id: "Subject", label: I18n.get("Subject") },
    { id: "Description", label: I18n.get("Description") },
    {
      id: "ClosedDate",
      label: I18n.get("Closed Date"),
      renderCell: (params) => {
        const value = params.value;
        return value ? formatTimestamp(value) : ""; // Return empty string if no value
      },
    },
    { id: "Type", label: I18n.get("Type") },
    { id: "FSL_Subtype__c", label: I18n.get("Subtype") },
    { id: "Origin", label: I18n.get("Origin") },
    {
      id: "LastModifiedDate",
      label: I18n.get("Last Modified Date"),
      renderCell: (params) => formatTimestamp(params.value),
    },
  ];
  const tlmGetSalesforceCaseInfo = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/salesforce/case/info",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            case_number: data.case_number,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };

  useEffect(() => {
    if (data?.id) {
      tlmGetSalesforceCaseInfo();
    }
  }, [data.id]);

  const isMobileDevice = useMediaQuery("(max-width:600px)");

  if (!state.fetched || state.audit.length === 0) {
    return (
      <Card>
        <CardContent sx={{ padding: "1rem", "&:last-child":{padding:"1rem"} }}>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
           // sx={{ pb: 2 }}
          >
            <Typography variant="h5">{I18n.get("Case Info")}</Typography>
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoData />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ padding: "1rem", "&:last-child":{padding:"1rem"} }}>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          //sx={{ pb: 2 }}
        >
          <Typography variant="h5">{I18n.get("Case Info")}</Typography>
        </Stack>
        <TableContainer>
          <Table>
          <TableBody>
  {headerCells.map((column, colIndex) => (
    <React.Fragment key={colIndex}>
      <TableCell
        style={{
          whiteSpace: "normal",
          wordWrap: "break-word",
          padding: "0px",
          borderBottom: "1px solid #e0e0e0",
          fontSize: "14px",
          lineHeight: 1.6,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: "15px",
          }}
        >
          {column.label}:
        </Typography>
        {column.renderCell ? (
          column.renderCell({
            value: state.audit[column.id],
            row: state.audit,
          })
        ) : state.audit[column.id] !== undefined &&
          state.audit[column.id] !== null &&
          state.audit[column.id] !== "N/A" ? (
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
            }}
          >
            {state.audit[column.id]}
          </Typography>
        ) : (
          ""
        )}
      </TableCell>
      {(colIndex + 1) % (isMobileDevice ? 1 : 2) === 0 && (
        <TableRow key={`spacer-${colIndex}`}></TableRow>
      )}
    </React.Fragment>
  ))}
</TableBody>

          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default CaseInfo;
